// @catalog F.030
// useProductDetailDrawerData — Encapsula los 4 fetches + PATCH del
// ProductDetailDrawer. Extraído para mantener P7 (<=500 líneas) en el
// orchestrator. Reusa los MISMOS endpoints del dialog viejo:
//   GET    /forecasting/product-detail/:id
//   GET    /forecasting/category-competitors/:id
//   GET    /forecasting/product-image/:id
//   GET    /forecasting/product-sales-by-store/:id?months=4
//   PATCH  /forecasting/product-mapping/:id/purchase-cant-user

import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../../services/apiClient";
import type {
  CategoryCompetitorsData,
  DetailData,
} from "../productDetailTypes";
import type { StoreSalesData } from "./TabPorTienda";

export interface StoreSalesErrorState {
  message: string;
}

export interface UseProductDetailDrawerDataResult {
  data: DetailData | null;
  loading: boolean;
  error: string | null;
  competitors: CategoryCompetitorsData | null;
  competitorsLoading: boolean;
  productImage: string | null;
  storeSales: StoreSalesData | null;
  storeSalesLoading: boolean;
  storeSalesError: StoreSalesErrorState | null;
  saving: boolean;
  saved: boolean;
  savePurchaseCant: (value: number | null) => Promise<void>;
}

export function useProductDetailDrawerData(
  open: boolean,
  unifiedProductId: string | null,
): UseProductDetailDrawerDataResult {
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [competitors, setCompetitors] =
    useState<CategoryCompetitorsData | null>(null);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);

  const [productImage, setProductImage] = useState<string | null>(null);

  const [storeSales, setStoreSales] = useState<StoreSalesData | null>(null);
  const [storeSalesLoading, setStoreSalesLoading] = useState(false);
  const [storeSalesError, setStoreSalesError] =
    useState<StoreSalesErrorState | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Fetch: detalle del producto ─────────────────────────────────────
  useEffect(() => {
    let active = true;
    async function load() {
      if (!open || !unifiedProductId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<{ status: string; data: DetailData }>(
          `/forecasting/product-detail/${encodeURIComponent(unifiedProductId)}`,
        );
        const json = res.data;
        if (json?.status === "success" && json.data) {
          if (active) setData(json.data);
        } else {
          throw new Error("Respuesta invalida");
        }
      } catch (e) {
        if (active) setError("No se pudo cargar el detalle del producto");
        console.error("Product detail fetch error:", e);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  // Fetch: competidores en categoría ────────────────────────────────
  useEffect(() => {
    let active = true;
    async function loadCompetitors() {
      if (!open || !unifiedProductId) return;
      setCompetitorsLoading(true);
      try {
        const res = await apiClient.get<{
          status: string;
          data: CategoryCompetitorsData;
        }>(
          `/forecasting/category-competitors/${encodeURIComponent(unifiedProductId)}`,
        );
        const json = res.data;
        if (json?.status === "success" && json.data) {
          if (active) setCompetitors(json.data);
        }
      } catch {
        /* silently ignore — competitors are supplemental info */
      } finally {
        if (active) setCompetitorsLoading(false);
      }
    }
    loadCompetitors();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  // Fetch: imagen del producto ──────────────────────────────────────
  useEffect(() => {
    let active = true;
    setProductImage(null);
    async function loadImage() {
      if (!open || !unifiedProductId) return;
      try {
        const res = await apiClient.get<{
          status: string;
          data: { imageUrl?: string };
        }>(
          `/forecasting/product-image/${encodeURIComponent(unifiedProductId)}`,
          { timeout: 10000 },
        );
        const json = res.data;
        if (json?.status === "success" && json.data?.imageUrl) {
          if (active) setProductImage(json.data.imageUrl);
        }
      } catch {
        /* ignore */
      }
    }
    loadImage();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  // Fetch: ventas por tienda (últimos 4 meses) ──────────────────────
  useEffect(() => {
    let active = true;
    async function loadStoreSales() {
      if (!open || !unifiedProductId) return;
      setStoreSalesLoading(true);
      setStoreSalesError(null);
      try {
        const res = await apiClient.get<{
          status: string;
          data: StoreSalesData;
        }>(
          `/forecasting/product-sales-by-store/${encodeURIComponent(unifiedProductId)}?months=4`,
        );
        const json = res.data;
        if (json?.status === "success" && json.data) {
          if (active) setStoreSales(json.data);
        } else {
          throw new Error("Respuesta invalida");
        }
      } catch (loadError) {
        if (active) {
          setStoreSales(null);
          setStoreSalesError({
            message:
              loadError instanceof Error
                ? loadError.message
                : "No se pudo cargar la fuente canonica",
          });
        }
      } finally {
        if (active) setStoreSalesLoading(false);
      }
    }
    loadStoreSales();
    return () => {
      active = false;
    };
  }, [open, unifiedProductId]);

  // PATCH: purchase-cant-user (override usuario) ───────────────────
  const savePurchaseCant = useCallback(
    async (value: number | null): Promise<void> => {
      if (!unifiedProductId) return;
      setSaving(true);
      try {
        const res = await apiClient.patch<{ status: string }>(
          `/forecasting/product-mapping/${encodeURIComponent(unifiedProductId)}/purchase-cant-user`,
          { value },
        );
        const json = res.data;
        if (json?.status === "success") {
          setData((prev) => {
            if (!prev?.mapping) return prev;
            return {
              ...prev,
              mapping: { ...prev.mapping, purchaseCantUser: value },
            };
          });
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        }
      } catch (e) {
        console.error("Error saving purchaseCantUser:", e);
      } finally {
        setSaving(false);
      }
    },
    [unifiedProductId],
  );

  return {
    data,
    loading,
    error,
    competitors,
    competitorsLoading,
    productImage,
    storeSales,
    storeSalesLoading,
    storeSalesError,
    saving,
    saved,
    savePurchaseCant,
  };
}
